import React, { useState, useEffect } from 'react';
import {
    useDataProvider,
    useTranslate,
    useNotify,
} from 'react-admin';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    Checkbox,
    TextField,
    Box,
    Typography,
    FormControlLabel,
    Switch,
    Chip,
    Stack,
    CircularProgress,
    Alert,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Search as SearchIcon,
    Email as EmailIcon,
    Sms as SmsIcon,
    WhatsApp as WhatsAppIcon,
    NotificationsActive as PushIcon,
    Chat as SlackIcon,
} from '@mui/icons-material';

interface AddMembersDialogProps {
    open: boolean;
    onClose: () => void;
    onAdd: (userIds: string[], channelsOverride?: string[]) => void;
    existingMemberIds: string[];
    groupChannels: string[];
}

const channelIcons: Record<string, React.ReactElement> = {
    email: <EmailIcon sx={{ fontSize: 16 }} />,
    sms: <SmsIcon sx={{ fontSize: 16 }} />,
    whatsapp: <WhatsAppIcon sx={{ fontSize: 16 }} />,
    push: <PushIcon sx={{ fontSize: 16 }} />,
    slack: <SlackIcon sx={{ fontSize: 16 }} />,
};

export const AddMembersDialog: React.FC<AddMembersDialogProps> = ({
    open,
    onClose,
    onAdd,
    existingMemberIds,
    groupChannels,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const dataProvider = useDataProvider();
    const translate = useTranslate();
    const notify = useNotify();
    
    const [users, setUsers] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [useOverride, setUseOverride] = useState(false);
    const [channelsOverride, setChannelsOverride] = useState<string[]>(groupChannels);
    
    useEffect(() => {
        if (open) {
            loadUsers();
        }
    }, [open]);
    
    const loadUsers = async () => {
        setLoading(true);
        try {
            const { data } = await dataProvider.getList('backend-users', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'name', order: 'ASC' },
                filter: { active: true },
            });
            
            // Filter out existing members
            const availableUsers = data.filter(
                (user: any) => !existingMemberIds.includes(user.id)
            );
            setUsers(availableUsers);
        } catch (error) {
            notify('resources.notification-groups.members.loadUsersError', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };
    
    const handleToggleUser = (userId: string) => {
        setSelectedUsers(prev => {
            if (prev.includes(userId)) {
                return prev.filter(id => id !== userId);
            } else {
                return [...prev, userId];
            }
        });
    };
    
    const handleToggleAll = () => {
        if (selectedUsers.length === filteredUsers.length) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers(filteredUsers.map(u => u.id));
        }
    };
    
    const handleToggleChannel = (channel: string) => {
        setChannelsOverride(prev => {
            if (prev.includes(channel)) {
                return prev.filter(c => c !== channel);
            } else {
                return [...prev, channel];
            }
        });
    };
    
    const handleAdd = () => {
        if (selectedUsers.length === 0) {
            notify('resources.notification-groups.members.selectAtLeastOne', { type: 'warning' });
            return;
        }
        
        onAdd(selectedUsers, useOverride ? channelsOverride : undefined);
        handleClose();
    };
    
    const handleClose = () => {
        setSelectedUsers([]);
        setSearch('');
        setUseOverride(false);
        setChannelsOverride(groupChannels);
        onClose();
    };
    
    const filteredUsers = users.filter(user => 
        user.name?.toLowerCase().includes(search.toLowerCase()) ||
        user.email?.toLowerCase().includes(search.toLowerCase())
    );
    
    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMobile}>
            <DialogTitle sx={{ wordBreak: 'break-word' }}>
                {translate('resources.notification-groups.members.addDialog.title')}
            </DialogTitle>
            <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        placeholder={translate('resources.notification-groups.members.addDialog.search')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                        }}
                    />
                </Box>
                
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : filteredUsers.length === 0 ? (
                    <Alert severity="info">
                        {translate('resources.notification-groups.members.addDialog.noUsers')}
                    </Alert>
                ) : (
                    <>
                        <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                {translate('resources.notification-groups.members.addDialog.selectUsers', {
                                    count: selectedUsers.length
                                })}
                            </Typography>
                            <Button size="small" onClick={handleToggleAll}>
                                {selectedUsers.length === filteredUsers.length
                                    ? translate('ra.action.unselect_all')
                                    : translate('ra.action.select_all')}
                            </Button>
                        </Box>
                        
                        <List sx={{ 
                            maxHeight: 300, 
                            overflow: 'auto', 
                            border: '1px solid rgba(0,0,0,0.1)', 
                            borderRadius: 1 
                        }}>
                            {filteredUsers.map(user => (
                                <ListItem key={user.id} disablePadding>
                                    <ListItemButton onClick={() => handleToggleUser(user.id)}>
                                        <ListItemIcon>
                                            <Checkbox
                                                edge="start"
                                                checked={selectedUsers.includes(user.id)}
                                                tabIndex={-1}
                                                disableRipple
                                            />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={user.name}
                                            secondary={user.email}
                                            primaryTypographyProps={{ noWrap: true }}
                                            secondaryTypographyProps={{ noWrap: true }}
                                            sx={{ minWidth: 0 }}
                                        />
                                        {user.roleId && (
                                            <Chip label={user.roleId} size="small" />
                                        )}
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </>
                )}
                
                <Box sx={{ mt: 3, p: 2, backgroundColor: '#F5F5F5', borderRadius: 1 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={useOverride}
                                onChange={(e) => setUseOverride(e.target.checked)}
                            />
                        }
                        label={translate('resources.notification-groups.members.addDialog.overrideChannels')}
                    />
                    
                    {useOverride && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                {translate('resources.notification-groups.members.addDialog.selectChannels')}
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ rowGap: 1 }}>
                                {['email', 'sms', 'whatsapp', 'push', 'slack'].map(channel => (
                                    <Chip
                                        key={channel}
                                        icon={channelIcons[channel]}
                                        label={translate(`resources.notification-groups.channels.${channel}`)}
                                        onClick={() => handleToggleChannel(channel)}
                                        color={channelsOverride.includes(channel) ? 'primary' : 'default'}
                                        variant={channelsOverride.includes(channel) ? 'filled' : 'outlined'}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>
                    {translate('ra.action.cancel')}
                </Button>
                <Button 
                    onClick={handleAdd} 
                    variant="contained"
                    disabled={selectedUsers.length === 0}
                >
                    {translate('resources.notification-groups.members.addDialog.add', {
                        count: selectedUsers.length
                    })}
                </Button>
            </DialogActions>
        </Dialog>
    );
};